import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  BlockStateSourceError,
  type BlockStateSource,
} from "../../../lib/blockStateSource";
import {
  createBlockStateChangeMessage,
  createBlockStateInitMessage,
  createBlockStateResultMessage,
  isBlockStateOps,
  type BlockStateViewer,
  type ParentToBlockMessage,
} from "../../../lib/htmlBlockRuntime";

/**
 * Relays `cohesive.state` between a block frame and its host source: the
 * full state on every runtime `ready`, each change after that, and the
 * answer to every `state-ops`. Without a source the block gets an empty
 * read-only state, which yields to a snapshot baked into the document.
 * `viewer` is the one baked into the document, kept by that empty state.
 */
export function useBlockStateBridge(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  source: BlockStateSource | undefined,
  viewer: BlockStateViewer | null = null,
) {
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const viewerRef = useRef(viewer);
  viewerRef.current = viewer;
  const readyRef = useRef(false);
  const writerRef = useRef({});
  const [unavailable, setUnavailable] = useState(false);

  const post = useCallback(
    (msg: ParentToBlockMessage) => {
      iframeRef.current?.contentWindow?.postMessage(msg, "*");
    },
    [iframeRef],
  );

  const sendInit = useCallback(() => {
    const current = sourceRef.current;
    if (!current) {
      post(
        createBlockStateInitMessage({
          entries: [],
          viewer: viewerRef.current,
          readonly: true,
          fallback: true,
        }),
      );
      return;
    }
    const snapshot = current.getSnapshot();
    if (snapshot) post(createBlockStateInitMessage(snapshot));
  }, [post]);

  useEffect(() => {
    setUnavailable(false);
    if (!source) return;
    // Subscribe first: a lazy source only holds its mirror once subscribed.
    const unsubscribe = source.subscribe((event) => {
      if (event.type === "unavailable") {
        setUnavailable(event.unavailable);
        return;
      }
      if (!readyRef.current) return;
      // This frame's own acked rows reach it through state-result; applied
      // here too, an increment still pending in the frame would count twice.
      if (event.type === "change" && event.writer === writerRef.current) {
        return;
      }
      post(
        event.type === "reset"
          ? createBlockStateInitMessage(event.snapshot)
          : createBlockStateChangeMessage(event.changes),
      );
    });
    setUnavailable(source.isUnavailable?.() ?? false);
    if (readyRef.current) sendInit();
    return unsubscribe;
  }, [source, post, sendInit]);

  const onRuntimeReady = useCallback(() => {
    readyRef.current = true;
    sendInit();
  }, [sendInit]);

  const onStateOps = useCallback(
    (reqId: string, ops: unknown) => {
      if (!isBlockStateOps(ops)) {
        post(
          createBlockStateResultMessage({
            reqId,
            ok: false,
            error: { code: "invalid", message: "Malformed state ops." },
          }),
        );
        return;
      }
      const current = sourceRef.current;
      if (!current) {
        post(
          createBlockStateResultMessage({
            reqId,
            ok: false,
            error: { code: "readonly", message: "This view is read-only." },
          }),
        );
        return;
      }
      current.submit(ops, writerRef.current).then(
        (rows) =>
          post(createBlockStateResultMessage({ reqId, ok: true, rows })),
        (err: unknown) =>
          post(
            createBlockStateResultMessage({
              reqId,
              ok: false,
              error:
                err instanceof BlockStateSourceError
                  ? {
                      code: err.code,
                      message: err.message,
                      retryAfterMs: err.retryAfterMs,
                    }
                  : { code: "failed", message: "The write was not saved." },
            }),
          ),
      );
    },
    [post],
  );

  return useMemo(
    () => ({ onRuntimeReady, onStateOps, unavailable }),
    [onRuntimeReady, onStateOps, unavailable],
  );
}
